import { dummyUser } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Dummy User Service
 * Handles all dummy user admin API calls.
 *
 * API Endpoints:
 * - GET /api/v1/admin/dummy-user/list - Get dummy user list
 * - GET /api/v1/admin/dummy-user/get - Get single dummy user
 * - POST /api/v1/admin/dummy-user/add - Add new dummy user
 * - PUT /api/v1/admin/dummy-user/update - Update dummy user
 * - PUT /api/v1/admin/dummy-user/update-status - Toggle online/offline status
 * - DELETE /api/v1/admin/dummy-user/delete - Delete dummy user
 */
class DummyUserService {
  /**
   * @name getList
   * @description Fetches dummy user list from admin API with pagination, search and online status filter.
   * @param {Object} params - Query parameters (page, limit, search, online_status)
   * @returns {Promise<Object>} The response data containing the dummy user list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(dummyUser.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single dummy user by UUID.
   * @param {string} uuid - Dummy User UUID
   * @returns {Promise<Object>} The response data containing dummy user details.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(dummyUser.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name add
   * @description Creates a new dummy user.
   * @param {Object} data - first_name, last_name, email, address, latitude, longitude
   * @returns {Promise<Object>} The response data.
   */
  static async add(data) {
    try {
      const response = await axios.post(dummyUser.add, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name update
   * @description Updates an existing dummy user's editable details.
   * @param {Object} data - uuid, first_name, last_name, email, address, latitude, longitude
   * @returns {Promise<Object>} The response data.
   */
  static async update(data) {
    try {
      const response = await axios.put(dummyUser.update, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name updateStatus
   * @description Toggles the online/offline status of a dummy user.
   * @param {string} uuid - Dummy User UUID
   * @param {boolean} isOnline - New online status
   * @returns {Promise<Object>} The response data.
   */
  static async updateStatus(uuid, isOnline) {
    try {
      const response = await axios.put(dummyUser.updateStatus, { uuid, is_online: isOnline });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name delete
   * @description Deletes (soft) a dummy user by UUID.
   * @param {string} uuid - Dummy User UUID
   * @returns {Promise<Object>} The response data.
   */
  static async delete(uuid) {
    try {
      const response = await axios.delete(dummyUser.delete, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { DummyUserService };
